import { Column, useTable } from 'react-table'
import { FunctionComponent, useMemo } from 'react'
import styled, { css } from 'styled-components'
import type {
  ValidAPIResponse,
  Table as TableType,
  DataRow,
  OutcomeType,
} from '@hooks/useSportsStandings'
import TeamCell from './TeamCell'
import NumberCell from './NumberCell'
import OutcomeCell from './OutcomeCell'
import HeaderCell from './HeaderCell'

type TableProps = Pick<ValidAPIResponse, 'header' | 'outcomeTypeColors'> &
  Pick<TableType, 'title' | 'dataRows' | 'legend'>

interface TableHeaderProps {
  columnType: string
}

type StyledTableHeaderProps = Pick<TableHeaderProps, 'columnType'>
type StyledTableCellProps = Pick<TableHeaderProps, 'columnType'>

const StyledTableWrapper = styled.div``

const StyledTable = styled.table`
  width: 100%;
  height: 100%;
  border-collapse: collapse;
`

const StyledTableRow = styled.tr`
  ${({
    theme: {
      colors: {
        grey: { grey_30 },
      },
    },
  }) => css`
    border-bottom: 1px solid ${grey_30};
  `}
`

const StyledTableCell = styled.td<StyledTableCellProps>`
  padding: 0;
  height: 100%;
  ${({ columnType }) => {
    return css`
      padding: 0;
      width: ${columnType === 'team.name'
        ? 'auto'
        : columnType === 'outcomeType'
        ? '6px'
        : '35px'};
    `
  }}
`

const StyledTableHeader = styled.th<StyledTableHeaderProps>`
  ${({ columnType }) => {
    return css`
      padding: 0;
      width: ${columnType === 'team.name'
        ? 'auto'
        : columnType === 'outcomeType'
        ? '6px'
        : '35px'};
    `
  }}
`

const StyledTableBody = styled.tbody``

const StyledTableHead = styled.thead``

const StyledTableTitle = styled.div`
  ${({
    theme: {
      fonts: { sourceSansPro },
      colors: {
        grey: { grey_100 },
      },
    },
  }) => css`
    font-family: ${sourceSansPro};
    font-size: 20px;
    line-height: 1.25;
    font-weight: 700;
    color: ${grey_100};
    letter-spacing: 0.01em;
    margin-bottom: 8px;
  `}
`

const Table: FunctionComponent<TableProps> = ({
  title,
  dataRows,
  header,
  legend,
  outcomeTypeColors,
}) => {
  const columns = useMemo(
    () =>
      header.map((headerItem) => ({
        Header: () => (
          <HeaderCell columnType={headerItem.id}>{headerItem.name}</HeaderCell>
        ),
        accessor:
          headerItem.id === 'team'
            ? ('team.name' as keyof DataRow)
            : headerItem.id,

        ...(headerItem.id === 'team'
          ? {
              Cell: ({
                cell: {
                  row: {
                    original: {
                      team: { name: teamName, logoSrc: teamLogoSrc },
                    },
                  },
                },
              }: {
                cell: { row: { original: DataRow } }
              }) => <TeamCell teamLogoSrc={teamLogoSrc} teamName={teamName} />,
            }
          : headerItem.id === 'outcomeType'
          ? {
              Cell: ({ value }: { value: OutcomeType }) => (
                <OutcomeCell
                  title={
                    value ? legend.find((item) => !!item[value])?.[value] : ''
                  }
                  backgroundColor={
                    value ? outcomeTypeColors[value] : 'transparent'
                  }
                />
              ),
            }
          : {
              Cell: ({
                value,
                row: {
                  original: { outcomeType },
                },
              }: {
                value: string
                row: {
                  original: {
                    outcomeType: OutcomeType
                  }
                }
              }) => (
                <NumberCell
                  title={
                    outcomeType
                      ? legend.find((item) => !!item[outcomeType])?.[
                          outcomeType
                        ]
                      : ''
                  }
                  columnType={headerItem.id}>
                  {value}
                </NumberCell>
              ),
            }),
      })) as readonly Column<DataRow>[],
    [header, outcomeTypeColors, legend]
  )

  const data = useMemo(
    () =>
      dataRows.map(
        ({ outcomeType, rank, points, gamesPlayed, goalDifference, team }) => ({
          outcomeType: outcomeType,
          rank,
          points,
          gamesPlayed,
          goalDifference,
          team,
        })
      ),
    [dataRows]
  )

  const tableInstance = useTable<DataRow>({ columns, data })

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance

  return (
    <StyledTableWrapper>
      <StyledTableTitle>{title}</StyledTableTitle>
      <StyledTable {...getTableProps()}>
        <StyledTableHead>
          {headerGroups.map((headerGroup) => {
            const { key, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps()
            return (
              <StyledTableRow key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key, ...restColumn } = column.getHeaderProps()
                  return (
                    <StyledTableHeader
                      key={key}
                      {...restColumn}
                      columnType={column.id}>
                      {column.render('Header')}
                    </StyledTableHeader>
                  )
                })}
              </StyledTableRow>
            )
          })}
        </StyledTableHead>
        <StyledTableBody {...getTableBodyProps}>
          {rows.map((row) => {
            prepareRow(row)
            const { key, ...restRowProps } = row.getRowProps()
            return (
              <StyledTableRow key={key} {...restRowProps}>
                {row.cells.map((cell) => {
                  const { key, ...restCellProps } = cell.getCellProps()
                  return (
                    <StyledTableCell
                      key={key}
                      {...restCellProps}
                      columnType={cell.column.id}>
                      {cell.render('Cell')}
                    </StyledTableCell>
                  )
                })}
              </StyledTableRow>
            )
          })}
        </StyledTableBody>
      </StyledTable>
    </StyledTableWrapper>
  )
}

export default Table
